/**
 * Expense Routes Configuration - OPTIMIZED WITH LAZY LOADING
 * Expense management, accounting, budgets, reports
 * All 29 pages are lazy-loaded for bundle size reduction
 */
import React from 'react';
import { Route } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';

// LAZY LOAD all page components (29 pages)
// Accounting Pages
import ChartOfAccountsPage from '../pages/ChartOfAccountsPage';
import JournalEntriesPage from '../pages/JournalEntriesPage';
import JournalEntryFormPage from '../pages/JournalEntryFormPage';
import CostCentersPage from '../pages/accounting/CostCentersPage';
import FixedAssetsPage from '../pages/accounting/FixedAssetsPage';
import BankReconciliationPage from '../pages/accounting/BankReconciliationPage';

// Vendor Pages
import VendorsPage from '../pages/VendorsPage';
import VendorDetailPage from '../pages/VendorDetailPage';
import VendorFormPage from '../pages/VendorFormPage';

// Bills (AP)
import BillsPage from '../pages/BillsPage';
import BillDetailPage from '../pages/BillDetailPage';
import BillFormPage from '../pages/BillFormPage';
import BillPaymentPage from '../pages/BillPaymentPage';

// Invoices (AR)
import InvoicesPage from '../pages/InvoicesPage';
import InvoiceDetailPage from '../pages/InvoiceDetailPage';
import InvoiceFormPage from '../pages/InvoiceFormPage';
import InvoicePaymentPage from '../pages/InvoicePaymentPage';

// Expense Management
import ExpenseCategoriesPage from '../pages/ExpenseCategoriesPage';
import ExpensesPage from '../pages/ExpensesPage';
import ExpenseFormPage from '../pages/ExpenseFormPage';
import BudgetsPage from '../pages/BudgetsPage';
import ExpenseAnalyticsPage from '../pages/ExpenseAnalyticsPage';

// Reports
import APAgingReportPage from '../pages/reports/APAgingReportPage';
import ARAgingReportPage from '../pages/reports/ARAgingReportPage';
import TrialBalancePage from '../pages/reports/TrialBalancePage';
import FinancialReportsHub from '../pages/reports/FinancialReportsHub';
import BranchPLReport from '../pages/reports/BranchPLReport';
import ConsolidatedPLReport from '../pages/reports/ConsolidatedPLReport';
import InventoryValuationReport from '../pages/reports/InventoryValuationReport';
import SalesBookPage from '../pages/reports/SalesBookPage';
import PayrollCostReportPage from '../pages/reports/PayrollCostReportPage';
import StatementOfAccountPage from '../pages/reports/StatementOfAccountPage';

// Payroll
import PayrollDashboard from '../pages/payroll/PayrollDashboard';
import EmployeeSalariesPage from '../pages/payroll/EmployeeSalariesPage';
import EmployeeSalaryDetailPage from '../pages/payroll/EmployeeSalaryDetailPage';
import EmployeeLoansPage from '../pages/payroll/EmployeeLoansPage';
import LoanDetailPage from '../pages/payroll/LoanDetailPage';
import PayrollRunsPage from '../pages/payroll/PayrollRunsPage';
import PayrollRunDetail from '../pages/payroll/PayrollRunDetail';
import PayslipsPage from '../pages/payroll/PayslipsPage';

const adminRoles = ['admin', 'super_admin', 'system_admin', 'retail_admin'];
const superRoles = ['super_admin', 'system_admin', 'admin'];

/**
 * Expense/Accounting routes - Lazy-loaded components
 */
export const expenseRoutes = (
  <>
    {/* Accounting */}
    <Route path="/accounting/accounts" element={<ChartOfAccountsPage />} />
    <Route path="/accounting/cost-centers" element={<CostCentersPage />} />
    <Route path="/accounting/fixed-assets" element={<FixedAssetsPage />} />
    <Route path="/accounting/reconciliation" element={<BankReconciliationPage />} />
    <Route path="/accounting/journal-entries" element={<JournalEntriesPage />} />
    <Route path="/accounting/journal-entries/new" element={<JournalEntryFormPage />} />

    {/* Vendors */}
    <Route path="/accounting/vendors" element={<VendorsPage />} />
    <Route path="/accounting/vendors/new" element={<VendorFormPage />} />
    <Route path="/accounting/vendors/:id" element={<VendorDetailPage />} />
    <Route path="/accounting/vendors/:id/edit" element={<VendorFormPage />} />

    {/* Bills (AP) */}
    <Route path="/accounting/bills" element={<BillsPage />} />
    <Route path="/accounting/bills/new" element={<BillFormPage />} />
    <Route path="/accounting/bills/:id" element={<BillDetailPage />} />
    <Route path="/accounting/bills/:id/edit" element={<BillFormPage />} />
    <Route path="/accounting/bills/:id/payment" element={<BillPaymentPage />} />

    {/* Invoices (AR) */}
    <Route path="/accounting/invoices" element={<InvoicesPage />} />
    <Route path="/accounting/invoices/new" element={<InvoiceFormPage />} />
    <Route path="/accounting/invoices/:id" element={<InvoiceDetailPage />} />
    <Route path="/accounting/invoices/:id/edit" element={<InvoiceFormPage />} />
    <Route path="/accounting/invoices/:id/payment" element={<InvoicePaymentPage />} />

    {/* Accounting Reports */}
    <Route path="/accounting/reports/ap-aging" element={<APAgingReportPage />} />
    <Route path="/accounting/reports/ar-aging" element={<ARAgingReportPage />} />
    <Route path="/accounting/reports/trial-balance" element={<TrialBalancePage />} />

    {/* Expense Management */}
    <Route path="/expense-categories" element={<ExpenseCategoriesPage />} />
    <Route path="/expenses" element={<ExpensesPage />} />
    <Route path="/expenses/new" element={<ExpenseFormPage />} />
    <Route path="/expenses/:id/edit" element={<ExpenseFormPage />} />
    <Route path="/budgets" element={<BudgetsPage />} />
    <Route path="/expenses/analytics" element={<ExpenseAnalyticsPage />} />

    {/* Payroll */}
    <Route
      path="/payroll"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin']}>
          <PayrollDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payroll/salaries"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin']}>
          <EmployeeSalariesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payroll/salaries/:employeeId"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin']}>
          <EmployeeSalaryDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payroll/loans"
      element={
        <ProtectedRoute
          allowedRoles={[
            'super_admin',
            'system_admin',
            'retail_admin',
            'branch_admin',
            'staff',
            'admin'
          ]}
        >
          <EmployeeLoansPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payroll/loans/:loanId"
      element={
        <ProtectedRoute
          allowedRoles={[
            'super_admin',
            'system_admin',
            'retail_admin',
            'branch_admin',
            'staff',
            'admin'
          ]}
        >
          <LoanDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payroll/runs"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin']}>
          <PayrollRunsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/payroll/runs/:runId"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin']}>
          <PayrollRunDetail />
        </ProtectedRoute>
      }
    />
    <Route path="/payroll/payslips" element={<PayslipsPage />} />

    {/* Financial Reports */}
    <Route
      path="/reports"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin', 'retail_admin', 'admin']}>
          <FinancialReportsHub />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/pl-consolidated"
      element={
        <ProtectedRoute allowedRoles={superRoles}>
          <ConsolidatedPLReport />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/pl-branch"
      element={
        <ProtectedRoute allowedRoles={superRoles}>
          <BranchPLReport />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/inventory-valuation"
      element={
        <ProtectedRoute allowedRoles={superRoles}>
          <InventoryValuationReport />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/sales-book"
      element={
        <ProtectedRoute allowedRoles={superRoles}>
          <SalesBookPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/payroll-cost"
      element={
        <ProtectedRoute allowedRoles={['super_admin', 'system_admin']}>
          <PayrollCostReportPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports/statement-of-account"
      element={
        <ProtectedRoute allowedRoles={superRoles}>
          <StatementOfAccountPage />
        </ProtectedRoute>
      }
    />
  </>
);

export default expenseRoutes;
